import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

// Fix Leaflet icon issue in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom Icons
const createCustomIcon = (color, size = 16) => {
  return new L.DivIcon({
    className: 'custom-div-icon',
    html: `<div style="
      background: radial-gradient(circle, ${color}, ${color}dd);
      width: ${size}px; height: ${size}px; 
      border-radius: 50%; 
      border: 3px solid white; 
      box-shadow: 0 0 12px ${color}88, 0 2px 6px rgba(0,0,0,0.4);
      transition: all 0.3s ease;
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2]
  });
};

const iconAvailable = createCustomIcon('#10b981', 18);
const iconBusy = createCustomIcon('#f97316', 18);

function LiveMap() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default to Caracas
  const center = [10.4806, -66.9036];

  useEffect(() => {
    // Read from Firestore where the app actually writes driver locations
    const locationsRef = collection(db, 'driver_locations');
    const unsubscribe = onSnapshot(locationsRef, (snapshot) => {
      const driversArray = [];
      
      snapshot.forEach((doc) => {
        const d = doc.data();
        let lat = 0;
        let lng = 0;
        if (d.location) {
          lat = d.location.latitude;
          lng = d.location.longitude;
        }
        
        let timestamp = 0;
        if (d.updatedAt) {
          timestamp = d.updatedAt.toMillis ? d.updatedAt.toMillis() : d.updatedAt;
        }

        driversArray.push({
          id: doc.id,
          lat,
          lng,
          timestamp,
          status: d.status || 'available',
          speed: d.speed || 0,
        });
      });

      const activeDrivers = driversArray.filter(d => {
        if (typeof d.lat !== 'number' || typeof d.lng !== 'number') return false;
        if (isNaN(d.lat) || isNaN(d.lng)) return false;
        if (d.lat === 0 && d.lng === 0) return false;
        
        // Filter out drivers who haven't updated location in 5 minutes
        const FIVE_MINUTES = 5 * 60 * 1000;
        const now = Date.now();
        if (now - d.timestamp > FIVE_MINUTES) {
          return false;
        }

        return true; 
      });
      
      setDrivers(activeDrivers);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching Firestore locations:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const availableCount = drivers.filter(d => d.status === 'available').length;
  const busyCount = drivers.filter(d => d.status === 'busy').length;

  return (
    <div className="livemap-page animate-fade-in">
      {/* Floating Header */}
      <div className="map-overlay-header">
        <div className="card map-title-card">
          <h2 className="page-title" style={{marginBottom: '2px'}}>Mapa en Vivo</h2>
          <p className="text-secondary text-sm">Ubicación de conductores en tiempo real</p>
        </div>
        
        <div className="map-counters">
          <div className="map-counter-card">
            <div className="counter-dot counter-dot-green" />
            <div>
              <p className="text-xs text-secondary">Disponibles</p>
              <p className="counter-value" style={{color: 'var(--accent-green)'}}>{availableCount}</p>
            </div>
          </div>
          <div className="map-counter-card">
            <div className="counter-dot counter-dot-orange" />
            <div>
              <p className="text-xs text-secondary">En Viaje</p>
              <p className="counter-value" style={{color: 'var(--accent-orange)'}}>{busyCount}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="map-container-wrapper">
        {loading && (
          <div className="map-loading-overlay">
            <div className="spinner-lg" />
          </div>
        )}
        
        <MapContainer 
          center={center} 
          zoom={13} 
          style={{ height: '100%', width: '100%', zIndex: 1 }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            className="dark-map-tiles"
          />
          
          {drivers.map(driver => (
            <Marker 
              key={driver.id} 
              position={[driver.lat, driver.lng]}
              icon={driver.status === 'available' ? iconAvailable : iconBusy}
            >
              <Popup>
                <div style={{fontFamily: 'Inter, sans-serif', fontSize: '13px', minWidth: '160px'}}>
                  <p style={{fontWeight: 700, marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #eee', color: '#1a1a1a'}}>
                    Conductor {driver.id.slice(0,6)}...
                  </p>
                  <p style={{color: '#333', marginBottom: '2px'}}>
                    <strong>Estado:</strong> {driver.status === 'available' ? '🟢 Disponible' : '🟠 En Viaje'}
                  </p>
                  <p style={{color: '#333', marginBottom: '2px'}}>
                    <strong>Velocidad:</strong> {(driver.speed * 3.6).toFixed(0)} km/h
                  </p>
                  <p style={{color: '#888', fontSize: '11px', marginTop: '6px'}}>
                    Última señal: {new Date(driver.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}

export default LiveMap;
